/**
 * MarketIntelGateStatusPage — /omega/market-intel/gate-status
 *
 * AD-150 MarketIntelGate status page: shows current suppression threshold,
 * whether the gate is active (TAM data present), and cumulative suppressed
 * proposal count.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

interface GateStatusData {
  ad150_threshold_usd_cents: number;
  suppressed_sub_scale_total: number;
  gate_active: boolean;
  message: string;
}

export function MarketIntelGateStatusPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isLoading } = useQuery({
    queryKey: ['market-intel-gate-status'],
    queryFn: () => apiFetch<GateStatusData>('/market-intel/gate-status'),
    refetchInterval: 30_000,
  });

  const thresholdUsd = data?.ad150_threshold_usd_cents
    ? `$${(data.ad150_threshold_usd_cents / 100).toLocaleString()}`
    : '$1,000,000';

  return (
    <div className="omega-page">
      <h1>AD-150 Market Intel Gate</h1>
      <p>Proposals for verticals with TAM below {thresholdUsd} are suppressed.</p>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <div>
          <p>
            <strong>Gate Active:</strong>{' '}
            {data?.gate_active ? 'Yes' : 'No (no TAM data yet)'}
          </p>
          <p>
            <strong>Total Suppressed Proposals:</strong>{' '}
            {data?.suppressed_sub_scale_total ?? 0}
          </p>
          <p>
            <em>Suppressed proposals had markets too small for OMEGA to build for.</em>
          </p>
        </div>
      )}
      <Link to="/omega/market-intel">← Back to Market Intel</Link>
    </div>
  );
}
